import { ApplicationConfig, ErrorHandler, inject, LOCALE_ID, provideAppInitializer, provideZoneChangeDetection } from '@angular/core';
import { provideRouter, withInMemoryScrolling, withRouterConfig  } from '@angular/router';

import { routes } from './app.routes';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { GoogleAuthService } from '../auth/services/google-auth.service';
import { JWT_OPTIONS, JwtHelperService } from '@auth0/angular-jwt';
import { MAT_DIALOG_DEFAULT_OPTIONS } from '@angular/material/dialog';
import { ClientErrorInterceptor } from '../exception-handling/interceptors/client-error.interceptor';
import { authInterceptorFn } from '../auth/interceptors/auth.interceptor';
import { registerLocaleData } from '@angular/common';
import localeRo from '@angular/common/locales/ro';
import { AuthService } from '../auth/services/auth.service';
import { CookieConsentService } from '../gdpr/services/cookie-consent.service';
import { xsrfInterceptor } from '../auth/interceptors/xsrf.interceptor';

registerLocaleData(localeRo);

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(
      routes, 
      withRouterConfig({
        canceledNavigationResolution: 'computed'
      }),
      withInMemoryScrolling({
        scrollPositionRestoration: 'top',    // Scroll to top on navigation
        anchorScrolling: 'enabled'           // Support # anchors
      })
    ),
    provideAppInitializer(() => initializeGoogleApi(inject(GoogleAuthService))),
    provideAppInitializer(() => initEnsureSession(inject(AuthService))),
    provideAppInitializer(() => initCookieConsent(inject(AuthService), inject(CookieConsentService))),
    provideZoneChangeDetection({ eventCoalescing: true }), 
    provideHttpClient(      
      withInterceptors([xsrfInterceptor, authInterceptorFn/* deviceIdInterceptor*/])
    ),
    {
      provide: ErrorHandler, useClass: ClientErrorInterceptor
    },
    { 
      provide: JWT_OPTIONS, useValue: {}
    },
    JwtHelperService,
    {
      provide: MAT_DIALOG_DEFAULT_OPTIONS,
      useValue: {
        disableClose: true,
        closeOnNavigation: false,
      }
    },
    { provide: LOCALE_ID, useValue: 'ro' } 
  ]
};

export function initializeGoogleApi(googleAuthService: GoogleAuthService): Promise<void> {
  // Return the promise to ensure Angular waits for its
  return googleAuthService.loadGoogleScript().catch(error => {
    console.error('Error loading Google API during initialization:', error);
    // Still resolve so the app continues to load
    return Promise.resolve();
  });
}


export function initEnsureSession(auth: AuthService) : Promise<boolean> {
  return auth.ensureSession(); // Promise<boolean> e ok
}

export function initCookieConsent(
  authService: AuthService, 
  cookieConsentService: CookieConsentService
): Promise<void> {
  let result = async () => {
    try {
      // Check current auth status (this should be resolved by now)
      const isLoggedIn = authService.isAuthenticated(); // Or however you check auth status
      
      // Initialize cookie consent
      await cookieConsentService.checkAndShowConsent(isLoggedIn);
    } catch (error) {
      console.error('Error initializing cookie consent:', error);
      // Fallback to anonymous user
      await cookieConsentService.checkAndShowConsent(false);
    }
  };

  return result();
}